import React, { useState, useEffect, useRef } from 'react';
import { PropertyDetails, EstimationResult } from '../types';
import { estimationService } from '../services/estimationService';
import { Loader2, TrendingUp, Sparkles, Minus, Plus, Building, Home, MapPin, Lightbulb, Moon } from 'lucide-react';
import { useModal } from './ui/ModalProvider';

interface CounterProps { 
  label: string; 
  value: number; 
  field: keyof PropertyDetails; 
  min?: number; 
  suffix?: string;
  step?: number;
  onIncrement: (field: keyof PropertyDetails, step?: number) => void;
  onDecrement: (field: keyof PropertyDetails, min?: number, step?: number) => void;
  onManualInput: (e: React.ChangeEvent<HTMLInputElement>, field: keyof PropertyDetails) => void;
}

const Counter: React.FC<CounterProps> = ({ 
  label, 
  value, 
  field, 
  min = 0, 
  suffix = '', 
  step = 1,
  onIncrement,
  onDecrement,
  onManualInput
}) => (
  <div className="flex flex-col">
    <label className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">{label}</label>
    <div className="flex items-center bg-slate-50 rounded-lg p-1 border border-slate-200">
      <button 
        type="button"
        onClick={() => onDecrement(field, min, step)}
        className="w-10 h-10 flex items-center justify-center bg-white rounded-md shadow-sm text-slate-600 hover:text-amber-600 transition-colors hover:shadow active:scale-95 border border-slate-100"
      >
        <Minus size={16} />
      </button>
      <div className="flex-1 text-center font-semibold text-lg text-slate-900 flex items-center justify-center">
          <input 
              type="number" 
              value={value} 
              onChange={(e) => onManualInput(e, field)}
              onFocus={(e) => e.target.select()}
              className="w-16 text-center bg-transparent outline-none appearance-none m-0 p-0 text-base"
          />
          {suffix && <span className="text-slate-400 text-sm ml-1">{suffix}</span>}
      </div>
      <button 
        type="button"
        onClick={() => onIncrement(field, step)}
        className="w-10 h-10 flex items-center justify-center bg-white rounded-md shadow-sm text-slate-600 hover:text-amber-600 transition-colors hover:shadow active:scale-95 border border-slate-100"
      >
        <Plus size={16} />
      </button>
    </div>
  </div>
);

interface EstimationPanelProps {
  initialData?: PropertyDetails;
  onResult?: (result: EstimationResult) => void;
  showInModal?: boolean;
  onClose?: () => void;
  /** When true, hide the panel title (e.g. when embedded in EstimationPanelWithTabs). */
  hideTitle?: boolean;
}

export const EstimationPanel: React.FC<EstimationPanelProps> = ({ 
  initialData, 
  onResult,
  showInModal = false,
  onClose,
  hideTitle = false,
}) => {
  const { toast } = useModal();
  const [step, setStep] = useState<'form' | 'loading' | 'result'>('form');
  const [formData, setFormData] = useState<PropertyDetails>(initialData || {
    address: '',
    propertyType: 'apartment',
    bedrooms: 1,
    bathrooms: 1,
    area: 30,
    maxGuests: 2
  });
  const [result, setResult] = useState<EstimationResult | null>(null);

  // Mettre à jour formData si initialData change
  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
    }
  }, [initialData]);
  
  // Address Autocomplete State
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Auto-sync maxGuests when bedrooms change
  useEffect(() => {
    setFormData(prev => ({
        ...prev,
        maxGuests: prev.bedrooms * 2 > 0 ? prev.bedrooms * 2 : 2
    }));
  }, [formData.bedrooms]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [wrapperRef]);

  const handleAddressChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, address: value }));

    if (value.length > 3) {
      try {
        const response = await fetch(`https://data.geopf.fr/geocodage/search/?q=${encodeURIComponent(value)}&limit=5`);
        const data = await response.json();
        setSuggestions(data.features || []);
        setShowSuggestions(true);
      } catch (err) {
        console.error("Address API error", err);
      }
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const selectAddress = (feature: any) => {
    setFormData(prev => ({ ...prev, address: feature.properties.label }));
    setShowSuggestions(false);
  };

  const handleIncrement = (field: keyof PropertyDetails, step = 1) => {
    setFormData(prev => ({
      ...prev,
      [field]: (prev[field] as number) + step
    }));
  };

  const handleDecrement = (field: keyof PropertyDetails, min = 0, step = 1) => {
    setFormData(prev => ({
      ...prev,
      [field]: Math.max(min, (prev[field] as number) - step)
    }));
  };

  const handleManualInput = (e: React.ChangeEvent<HTMLInputElement>, field: keyof PropertyDetails) => {
    const val = parseInt(e.target.value);
    const finalVal = typeof val === 'number' && isNaN(val) ? 0 : val;
    setFormData(prev => ({ ...prev, [field]: finalVal }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.address) {
      toast.error("Merci de renseigner l'adresse.");
      return;
    }
    
    setStep('loading');
    try {
      const data = await estimationService.estimate({
        mode: 'internal',
        propertyDetails: formData,
      });
      setResult(data);
      setStep('result');
    } catch (error: any) {
      console.error(error);
      setStep('form');
      toast.error(error.message || "Erreur lors de l'estimation. Veuillez réessayer.");
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(amount);
  };

  const COMMISSION_RATE = 0.25;

  const rootClass = showInModal ? "p-6" : hideTitle ? "" : "p-8";
  return (
    <div className={rootClass}>
      {!showInModal && !hideTitle && (
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-900">Outil d'Estimation</h1>
          <p className="text-slate-500 mt-1">Estimez les revenus locatifs pour toute la France</p>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
        {step === 'form' && (
          <div className="p-8 lg:p-12">
            <form onSubmit={handleSubmit} className="space-y-8">
              
              <div className="relative" ref={wrapperRef}>
                <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">Adresse (France Entière)</label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <MapPin className="h-5 w-5 text-amber-600" />
                  </div>
                  <input
                    type="text"
                    required
                    autoComplete="off"
                    placeholder="Adresse complète..."
                    className="block w-full pl-12 pr-4 py-4 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 transition-all font-medium text-base"
                    value={formData.address}
                    onChange={handleAddressChange}
                  />
                </div>
                {showSuggestions && suggestions.length > 0 && (
                  <ul className="absolute z-50 w-full bg-white border border-slate-100 mt-2 rounded-xl shadow-xl max-h-60 overflow-auto divide-y divide-slate-50 py-2">
                    {suggestions.map((feature, idx) => (
                      <li 
                        key={idx}
                        onClick={() => selectAddress(feature)}
                        className="px-5 py-3 hover:bg-amber-50 cursor-pointer text-sm text-slate-700 flex items-center transition-colors"
                      >
                        <MapPin size={14} className="mr-3 text-slate-400" />
                        {feature.properties.label}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">Type</label>
                <div className="grid grid-cols-2 gap-4">
                  <div 
                    onClick={() => setFormData(prev => ({ ...prev, propertyType: 'apartment' }))}
                    className={`cursor-pointer border rounded-xl p-4 flex flex-col items-center justify-center transition-all ${formData.propertyType === 'apartment' ? 'border-amber-600 bg-amber-50 text-amber-700' : 'border-slate-200 bg-slate-50 text-slate-600 hover:border-slate-300'}`}
                  >
                    <Building className="mb-2 h-6 w-6" />
                    <span className="font-semibold text-sm">Appartement</span>
                  </div>
                  <div 
                    onClick={() => setFormData(prev => ({ ...prev, propertyType: 'house' }))}
                    className={`cursor-pointer border rounded-xl p-4 flex flex-col items-center justify-center transition-all ${formData.propertyType === 'house' ? 'border-amber-600 bg-amber-50 text-amber-700' : 'border-slate-200 bg-slate-50 text-slate-600 hover:border-slate-300'}`}
                  >
                    <Home className="mb-2 h-6 w-6" />
                    <span className="font-semibold text-sm">Maison</span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <Counter label="Surface" value={formData.area} field="area" min={9} suffix="m²" step={1} onIncrement={handleIncrement} onDecrement={handleDecrement} onManualInput={handleManualInput} />
                <Counter label="Chambres" value={formData.bedrooms} field="bedrooms" min={0} onIncrement={handleIncrement} onDecrement={handleDecrement} onManualInput={handleManualInput} />
                <Counter label="Salles de bain" value={formData.bathrooms} field="bathrooms" min={1} onIncrement={handleIncrement} onDecrement={handleDecrement} onManualInput={handleManualInput} />
                <Counter label="Voyageurs Max" value={formData.maxGuests || 2} field="maxGuests" min={1} onIncrement={handleIncrement} onDecrement={handleDecrement} onManualInput={handleManualInput} />
              </div>

              <div className="pt-4">
                <button type="submit" className="w-full h-14 text-lg rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-semibold flex items-center justify-center transition-colors">
                  Lancer Simulation <TrendingUp className="ml-2 h-5 w-5" />
                </button>
              </div>
            </form>
          </div>
        )}

        {step === 'loading' && (
          <div className="p-20 flex flex-col items-center justify-center text-center">
            <Loader2 className="h-16 w-16 text-amber-600 animate-spin mb-8" />
            <h3 className="text-xl font-bold text-slate-900">Calcul en cours...</h3>
            <p className="text-slate-500 text-sm mt-2">Génération de l'analyse marketing...</p>
          </div>
        )}

        {step === 'result' && result && (
          <div className="grid grid-cols-1 md:grid-cols-2">
             <div className="bg-slate-900 text-white p-10 flex flex-col justify-between">
                <div>
                    <h3 className="text-sm font-bold text-amber-500 uppercase tracking-widest mb-4">Résultat Brut</h3>
                    <div className="text-5xl font-bold mb-2">{formatCurrency(result.highEstimate)}</div>
                    <div className="text-xl text-slate-400 mb-6">Revenu Max Estimé</div>
                    
                    <div className="space-y-4">
                    <div className="bg-white/10 p-4 rounded-lg">
                        <div className="flex justify-between mb-1">
                            <span className="text-slate-300">Taux d'occupation</span>
                            <span className="font-bold text-amber-500">{result.occupancyRate}%</span>
                        </div>
                        <div className="w-full bg-slate-700 h-1 rounded-full">
                            <div className="bg-amber-500 h-1 rounded-full" style={{ width: `${result.occupancyRate}%` }}></div>
                        </div>
                    </div>

                    {result.averageNightlyPrice && (
                        <div className="bg-white/10 p-4 rounded-lg flex items-center justify-between">
                        <div className="flex items-center text-slate-300">
                            <Moon className="h-5 w-5 mr-3 text-amber-500" />
                            <span>Prix moyen / nuit</span>
                        </div>
                        <span className="font-bold text-2xl text-white">{formatCurrency(result.averageNightlyPrice)}</span>
                        </div>
                    )}
                    </div>
                </div>
             </div>
             
             <div className="p-10 bg-white">
                <h3 className="text-xl font-bold text-slate-900 mb-4">Détails Financiers</h3>
                <div className="space-y-4">
                    <div className="flex justify-between border-b pb-2">
                        <span className="text-slate-600">Moyenne Brute</span>
                        <span className="font-bold">{formatCurrency((result.lowEstimate + result.highEstimate) / 2)}</span>
                    </div>
                    <div className="flex justify-between border-b pb-2">
                        <span className="text-slate-600">Commission (25%)</span>
                        <span className="font-bold text-red-500">
                            - {formatCurrency(((result.lowEstimate + result.highEstimate) / 2) * COMMISSION_RATE)}
                        </span>
                    </div>
                    <div className="flex justify-between pt-2">
                        <span className="text-slate-900 font-bold">NET PROPRIÉTAIRE</span>
                        <span className="font-bold text-green-600 text-xl">
                            {formatCurrency(((result.lowEstimate + result.highEstimate) / 2) * (1 - COMMISSION_RATE))}
                        </span>
                    </div>
                </div>
                
                {/* Marketing Analysis Section */}
                {result.marketAnalysis && (
                    <div className="mt-8 bg-amber-50 p-4 rounded-lg border border-amber-200">
                        <div className="flex items-center mb-2">
                             <Lightbulb className="h-4 w-4 text-amber-600 mr-2" />
                             <h4 className="font-bold text-xs uppercase text-slate-900">Marketing IA</h4>
                        </div>
                        <p className="text-sm text-slate-800 font-medium leading-relaxed">
                            "{result.marketAnalysis}"
                        </p>
                    </div>
                )}
                
                <div className="mt-4 bg-slate-50 p-4 rounded-lg border border-slate-200">
                    <h4 className="font-bold text-xs uppercase text-slate-500 mb-2">Description Technique</h4>
                    <p className="text-sm text-slate-700 italic">"{result.description}"</p>
                </div>

                <div className="flex gap-3 mt-6">
                  <button 
                    onClick={() => setStep('form')} 
                    className="flex-1 py-3 border border-slate-300 rounded-lg text-slate-600 hover:bg-slate-50 transition-colors"
                  >
                    Nouvelle Simulation
                  </button>
                  {onResult && (
                    <button
                      onClick={() => {
                        onResult(result);
                        if (onClose) onClose();
                      }}
                      className="flex-1 py-3 bg-amber-600 text-white rounded-lg hover:bg-amber-700 font-medium shadow-sm transition-colors"
                    >
                      {showInModal ? 'Ajouter aux notes' : 'Utiliser ce résultat'}
                    </button>
                  )}
                </div>
             </div>
          </div>
        )}
      </div>
    </div>
  );
};
